import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../services/firebase";
import { authService } from "../services/api";

const AuthContext = createContext(null);
const AUTH_SYNC_TIMEOUT_MS = 12000;
const SESSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function withTimeout(promise, timeoutMs) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Authentication sync timed out. Please try again.")), timeoutMs);
    }),
  ]);
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const clearStoredAuth = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("authMode");
    localStorage.removeItem("authSessionExpiresAt");
  }, []);

  const isSessionExpired = useCallback(() => {
    const expiresAt = Number(localStorage.getItem("authSessionExpiresAt") || 0);
    return Boolean(expiresAt) && Date.now() > expiresAt;
  }, []);

  const hydrateCustomSession = useCallback(async () => {
    const storedToken = localStorage.getItem("authToken");
    const storedMode = localStorage.getItem("authMode");
    if (!storedToken || storedMode !== "custom") {
      return false;
    }

    if (isSessionExpired()) {
      clearStoredAuth();
      return false;
    }

    try {
      setSyncing(true);
      const response = await withTimeout(authService.getMe(), AUTH_SYNC_TIMEOUT_MS);
      const sessionUser = response.data.user;
      setAuthToken(storedToken);
      setUser(sessionUser);
      if (!localStorage.getItem("authSessionExpiresAt")) {
        localStorage.setItem("authSessionExpiresAt", String(Date.now() + SESSION_WINDOW_MS));
      }
      localStorage.setItem("user", JSON.stringify(sessionUser));
      setError("");
      return true;
    } catch (sessionError) {
      clearStoredAuth();
      setUser(null);
      setAuthToken(null);
      return false;
    } finally {
      setSyncing(false);
    }
  }, [clearStoredAuth, isSessionExpired]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setSyncing(false);
    }, 4000);

    const handleCustomLogin = (event) => {
      const token = event.detail?.token || localStorage.getItem("authToken");
      const sessionUser = event.detail?.user || JSON.parse(localStorage.getItem("user") || "null");
      if (!token || !sessionUser) return;
      setAuthToken(token);
      setUser(sessionUser);
      setError("");
      setLoading(false);
      setSyncing(false);
    };

    window.addEventListener("hexvault:custom-login", handleCustomLogin);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        const hasCustomSession = await hydrateCustomSession();
        if (hasCustomSession) {
          setLoading(false);
          return;
        }
        setUser(null);
        setAuthToken(null);
        setSyncing(false);
        clearStoredAuth();
        setLoading(false);
        return;
      }

      try {
        if (isSessionExpired()) {
          clearStoredAuth();
          await signOut(auth);
          setUser(null);
          setAuthToken(null);
          setSyncing(false);
          setLoading(false);
          return;
        }
        setSyncing(true);
        const token = await firebaseUser.getIdToken(true);
        localStorage.setItem("authToken", token);
        localStorage.setItem("authMode", "firebase");
        if (!localStorage.getItem("authSessionExpiresAt")) {
          localStorage.setItem("authSessionExpiresAt", String(Date.now() + SESSION_WINDOW_MS));
        }
        setAuthToken(token);

        const response = await withTimeout(authService.syncSession(), AUTH_SYNC_TIMEOUT_MS);
        const syncedUser = response.data.user;
        setUser(syncedUser);
        localStorage.setItem("user", JSON.stringify(syncedUser));
        setError("");
      } catch (syncError) {
        console.error(syncError);
        setUser(null);
        setAuthToken(null);
        clearStoredAuth();
        const message =
          syncError.response?.data?.message ||
          (syncError.message === "Network Error"
            ? "Could not connect to the HexVault server. Please try again later."
            : syncError.message) ||
          "Authentication sync failed";
        setError(message);
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error(signOutError);
        }
      } finally {
        setSyncing(false);
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("hexvault:custom-login", handleCustomLogin);
      unsubscribe();
    };
  }, [clearStoredAuth, hydrateCustomSession, isSessionExpired]);

  const logout = useCallback(async () => {
    if (auth.currentUser) {
      await signOut(auth);
    }
    setUser(null);
    setAuthToken(null);
    clearStoredAuth();
  }, [clearStoredAuth]);

  const value = useMemo(
    () => ({
      user,
      authToken,
      loading,
      syncing,
      error,
      logout,
      isAuthenticated: Boolean(user && authToken),
    }),
    [authToken, error, loading, logout, syncing, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
